import { useState, useEffect, useCallback } from 'react'
import { RefreshCw, Code, ChevronDown, ChevronRight, Search } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Endpoint {
  method: 'GET' | 'POST' | 'DELETE' | 'PUT'
  path: string
  description: string
  example: string
}

interface EndpointCategory {
  name: string
  endpoints: Endpoint[]
}

const METHOD_COLORS: Record<string, string> = {
  GET: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  POST: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  DELETE: 'bg-red-500/20 text-red-400 border-red-500/30',
  PUT: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
}

const API_DOCS: EndpointCategory[] = [
  {
    name: 'Auth',
    endpoints: [
      {
        method: 'POST',
        path: '/api/auth/login',
        description: 'Authenticate user and return JWT token pair',
        example: '{ "access_token": "eyJ...", "refresh_token": "eyJ...", "role": "admin" }',
      },
      {
        method: 'POST',
        path: '/api/auth/refresh',
        description: 'Refresh an expired access token using a valid refresh token',
        example: '{ "access_token": "eyJ...", "expires_in": 3600 }',
      },
    ],
  },
  {
    name: 'Signals',
    endpoints: [
      {
        method: 'GET',
        path: '/api/signals',
        description: 'Get aggregated technology signals with trend scores and velocity',
        example: '{ "signals": [{ "name": "Rust", "score": 87, "velocity": "+12%", "category": "language" }] }',
      },
      {
        method: 'GET',
        path: '/api/signals/top',
        description: 'Get top trending signals sorted by momentum',
        example: '{ "top": [{ "name": "WebAssembly", "mentions_7d": 342, "trend": "rising" }] }',
      },
    ],
  },
  {
    name: 'News',
    endpoints: [
      {
        method: 'GET',
        path: '/api/news',
        description: 'Fetch latest tech news articles from all monitored feeds',
        example: '{ "articles": [{ "title": "...", "source": "TechCrunch", "published": "2026-04-15T..." }] }',
      },
      {
        method: 'GET',
        path: '/api/news/sources',
        description: 'List all configured RSS feed sources and their status',
        example: '{ "sources": [{ "name": "Ars Technica", "url": "...", "last_fetch": "...", "status": "ok" }] }',
      },
    ],
  },
  {
    name: 'Cyber',
    endpoints: [
      {
        method: 'GET',
        path: '/api/cyber/cves',
        description: 'Get recent CVEs sorted by severity, with enrichment data',
        example: '{ "cves": [{ "id": "CVE-2026-1234", "cvss": 9.8, "summary": "...", "exploited": true }] }',
      },
      {
        method: 'GET',
        path: '/api/cyber/threats',
        description: 'Get active threat intelligence including campaigns and IOCs',
        example: '{ "threats": [{ "name": "APT-42", "severity": "critical", "targets": ["finance"] }] }',
      },
    ],
  },
  {
    name: 'Cloud',
    endpoints: [
      {
        method: 'GET',
        path: '/api/cloud/status',
        description: 'Get current status of all monitored cloud providers',
        example: '{ "providers": [{ "name": "AWS", "status": "operational", "incidents": 0 }] }',
      },
      {
        method: 'GET',
        path: '/api/cloud/pricing',
        description: 'Get latest cloud instance pricing comparisons',
        example: '{ "instances": [{ "type": "4vCPU/16GB", "aws": 0.166, "azure": 0.172, "gcp": 0.160 }] }',
      },
    ],
  },
  {
    name: 'Innovation',
    endpoints: [
      {
        method: 'GET',
        path: '/api/innovation',
        description: 'Get innovation radar items including startups and emerging tech',
        example: '{ "items": [{ "name": "Quantum Error Correction", "phase": "emerging", "impact": "high" }] }',
      },
    ],
  },
  {
    name: 'World',
    endpoints: [
      {
        method: 'GET',
        path: '/api/world/events',
        description: 'Get geopolitical events with tech industry impact assessment',
        example: '{ "events": [{ "title": "...", "region": "APAC", "severity": "high", "tech_impact": "..." }] }',
      },
      {
        method: 'GET',
        path: '/api/world/cables',
        description: 'Get submarine cable status and alert data',
        example: '{ "cables": [{ "name": "PEACE", "status": "operational", "landing_count": 15 }] }',
      },
    ],
  },
  {
    name: 'Trends',
    endpoints: [
      {
        method: 'GET',
        path: '/api/trends',
        description: 'Get technology trend data with historical time series',
        example: '{ "trends": [{ "topic": "GenAI", "mentions_30d": 4821, "direction": "growing" }] }',
      },
      {
        method: 'GET',
        path: '/api/trends/hype-cycle',
        description: 'Get technologies mapped to hype cycle phases',
        example: '{ "items": [{ "name": "LLMs", "phase": "slope_of_enlightenment", "maturity": 0.72 }] }',
      },
    ],
  },
  {
    name: 'Threats',
    endpoints: [
      {
        method: 'GET',
        path: '/api/threats/landscape',
        description: 'Get current threat landscape overview with risk scores',
        example: '{ "risk_level": "elevated", "active_campaigns": 12, "critical_vulns": 3 }',
      },
    ],
  },
  {
    name: 'Search',
    endpoints: [
      {
        method: 'GET',
        path: '/api/search',
        description: 'Full-text search across all articles, CVEs, and signals. Params: q, limit, offset',
        example: '{ "results": [{ "type": "article", "title": "...", "snippet": "...", "score": 0.95 }] }',
      },
    ],
  },
  {
    name: 'Watchlist',
    endpoints: [
      {
        method: 'GET',
        path: '/api/watchlist',
        description: 'Get all watchlist entities with 7-day mention counts',
        example: '{ "entities": [{ "name": "Kubernetes", "mentions_7d": 89, "trend": "+15%" }] }',
      },
      {
        method: 'POST',
        path: '/api/watchlist/add',
        description: 'Add a new entity to the watchlist',
        example: '{ "status": "added", "name": "Anthropic" }',
      },
      {
        method: 'DELETE',
        path: '/api/watchlist/remove',
        description: 'Remove an entity from the watchlist. Params: name',
        example: '{ "status": "removed", "name": "Anthropic" }',
      },
    ],
  },
  {
    name: 'Discord',
    endpoints: [
      {
        method: 'POST',
        path: '/api/discord/test',
        description: 'Send a test message to the configured Discord webhook',
        example: '{ "status": "sent", "webhook": "configured" }',
      },
      {
        method: 'GET',
        path: '/api/discord/history',
        description: 'Get history of sent Discord notifications',
        example: '{ "messages": [{ "type": "cve_alert", "sent_at": "...", "channel": "security" }] }',
      },
    ],
  },
  {
    name: 'System',
    endpoints: [
      {
        method: 'GET',
        path: '/api/health',
        description: 'Health check endpoint returning system status and uptime',
        example: '{ "status": "healthy", "uptime": "14d 6h", "feeds": 152, "last_sync": "2m ago" }',
      },
      {
        method: 'GET',
        path: '/api/system/stats',
        description: 'Get system statistics including article counts and storage usage',
        example: '{ "articles_total": 48210, "db_size_mb": 312, "feeds_active": 148 }',
      },
    ],
  },
  {
    name: 'Admin',
    endpoints: [
      {
        method: 'GET',
        path: '/api/admin/users',
        description: 'List all registered users (admin only)',
        example: '{ "users": [{ "email": "admin@co.com", "role": "admin", "mfa_enabled": true }] }',
      },
      {
        method: 'POST',
        path: '/api/admin/users',
        description: 'Create a new user account (admin only)',
        example: '{ "email": "new@co.com", "role": "viewer", "created": true }',
      },
      {
        method: 'DELETE',
        path: '/api/admin/users/{email}',
        description: 'Delete a user account (admin only)',
        example: '{ "deleted": true, "email": "user@co.com" }',
      },
      {
        method: 'PUT',
        path: '/api/admin/users/{email}/reset-mfa',
        description: 'Reset MFA for a user, returns new provisioning URI (admin only)',
        example: '{ "mfa_reset": true, "provisioning_uri": "otpauth://totp/..." }',
      },
    ],
  },
]

export default function ApiDocsPage() {
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {}
    API_DOCS.forEach(c => { init[c.name] = true })
    return init
  })

  const toggle = (name: string) => {
    setExpanded(prev => ({ ...prev, [name]: !prev[name] }))
  }

  const expandAll = () => {
    const next: Record<string, boolean> = {}
    API_DOCS.forEach(c => { next[c.name] = true })
    setExpanded(next)
  }

  const collapseAll = () => {
    const next: Record<string, boolean> = {}
    API_DOCS.forEach(c => { next[c.name] = false })
    setExpanded(next)
  }

  const lowerSearch = search.toLowerCase()
  const filteredDocs = API_DOCS.map(cat => ({
    ...cat,
    endpoints: cat.endpoints.filter(ep =>
      !search ||
      ep.path.toLowerCase().includes(lowerSearch) ||
      ep.description.toLowerCase().includes(lowerSearch) ||
      ep.method.toLowerCase().includes(lowerSearch)
    ),
  })).filter(cat => cat.endpoints.length > 0)

  const totalEndpoints = API_DOCS.reduce((sum, c) => sum + c.endpoints.length, 0)

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Code size={20} className="text-cyan" /> API Documentation
          </h1>
          <p className="text-[10px] text-text-secondary">
            {totalEndpoints} endpoints across {API_DOCS.length} categories — all responses are JSON
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={expandAll} className="btn-secondary text-xs">
            Expand All
          </button>
          <button type="button" onClick={collapseAll} className="btn-secondary text-xs">
            Collapse All
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search endpoints by path or description..."
          className="input py-2 text-xs pl-9 w-full"
        />
      </div>

      {/* Endpoint categories */}
      <div className="space-y-3">
        {filteredDocs.map(cat => (
          <div key={cat.name} className="card overflow-hidden">
            {/* Category header */}
            <button
              type="button"
              onClick={() => toggle(cat.name)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-surface-alt transition-colors"
            >
              <div className="flex items-center gap-2">
                {expanded[cat.name]
                  ? <ChevronDown size={14} className="text-text-muted" />
                  : <ChevronRight size={14} className="text-text-muted" />
                }
                <h2 className="text-sm font-semibold text-text-primary">{cat.name}</h2>
                <span className="text-[10px] text-text-muted">
                  {cat.endpoints.length} endpoint{cat.endpoints.length !== 1 ? 's' : ''}
                </span>
              </div>
            </button>

            {/* Endpoints */}
            {expanded[cat.name] && (
              <div className="border-t border-border">
                {cat.endpoints.map((ep, i) => (
                  <div
                    key={`${ep.method}-${ep.path}`}
                    className={clsx(
                      'px-4 py-3',
                      i < cat.endpoints.length - 1 && 'border-b border-border'
                    )}
                  >
                    <div className="flex items-start gap-3">
                      {/* Method badge */}
                      <span className={clsx(
                        'inline-flex items-center justify-center text-[10px] font-bold px-2 py-0.5 rounded border shrink-0 w-14 text-center',
                        METHOD_COLORS[ep.method]
                      )}>
                        {ep.method}
                      </span>

                      <div className="flex-1 min-w-0">
                        {/* Path */}
                        <code className="text-xs font-mono text-cyan">{ep.path}</code>
                        {/* Description */}
                        <p className="text-[11px] text-text-secondary mt-1">{ep.description}</p>
                        {/* Example response */}
                        <div className="mt-2 bg-surface-alt rounded-lg px-3 py-2">
                          <span className="text-[9px] text-text-muted uppercase tracking-wider font-semibold">Example Response</span>
                          <pre className="text-[10px] font-mono text-text-secondary mt-1 whitespace-pre-wrap break-all">
                            {ep.example}
                          </pre>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {filteredDocs.length === 0 && (
          <div className="card p-8 text-center">
            <p className="text-sm text-text-muted">No endpoints match your search.</p>
          </div>
        )}
      </div>
    </div>
  )
}
